import { useEffect } from 'react';
import { Routes, Route, useSearchParams } from 'react-router-dom';
import { useMachine } from '@xstate/react';
import { useNavigate } from 'react-router-dom';
import {
  authFSM,
  EventType,
  State,
  OAuthProvider,
  resolveOAuthErrorMessage,
  justLinkedMessage,
} from '../../fsm/auth';

import { message } from 'antd';

import Login from './pages/login';
import ResetPassword from './pages/reset-password';
import ActivateAccount from './pages/activate';
import { useCallback } from 'react';

/**
 * Stand-in for GET /api/auth/invitations/{token}, which doesn't exist yet.
 * A real implementation replaces this with a fetched
 * `{ customerName, accountExists }`; reading it from the URL instead is
 * what lets this screen actually be visited and reviewed before that
 * endpoint exists, rather than always showing an error state on load.
 * `?existingAccount=true` exercises the account-already-exists copy;
 * omitted, the page shows the brand-new-account path.
 */
const readInvitationDetailsFromQuery = (
  searchParams: URLSearchParams,
): { customerName: string; accountExists: boolean } => ({
  customerName: searchParams.get('customer') ?? 'your new customer',
  accountExists: searchParams.get('existingAccount') === 'true',
});

const AuthRoutes = () => {
  const [state, send] = useMachine(authFSM);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('token') ?? '';
  const { customerName, accountExists } =
    readInvitationDetailsFromQuery(searchParams);

  useEffect(() => {
    if (state.value.match(State.ERROR)) {
      messageApi.open({
        type: 'error',
        content: resolveOAuthErrorMessage(
          state.context.error,
          state.context.provider,
        ),
      });
    }
    if (state.value.match(State.EMAIL_SENT)) {
      messageApi.open({
        type: 'success',
        content: 'Password reset link sent to your email.',
      });
    }
    // AUTHENTICATED carries an unguarded `always` transition straight back
    // to idle (see that state's own comment) -- INITIAL is the value this
    // effect actually observes after a successful login, OAuth or not.
    // `justLinked` is only ever true coming out of an OAuth success, so a
    // password login (which never sets it) can't trigger this by accident.
    if (state.value.match(State.INITIAL) && state.context.justLinked) {
      messageApi.open({
        type: 'info',
        content: justLinkedMessage(state.context.provider),
      });
    }
  }, [state.value]);

  const handleLogin = useCallback(
    (email: string, password: string) => {
      send({
        type: EventType.LOGIN,
        payload: { email, password, navigate },
      });
    },
    [send, navigate],
  );

  const handleOAuthLogin = useCallback(
    (provider: OAuthProvider) => {
      send({
        type: EventType.OAUTH_LOGIN,
        payload: { provider, navigate },
      });
    },
    [send, navigate],
  );

  const handleResetPasswordRequest = useCallback(
    (email: string) => {
      send({
        type: EventType.RESET_PASSWORD_REQUEST,
        payload: { email },
      });
    },
    [send],
  );

  const handleResetPassword = useCallback(
    (password: string, token: string) => {
      send({
        type: EventType.RESET_PASSWORD,
        payload: { password, token, navigate },
      });
    },
    [send, navigate],
  );

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={
            <Login
              isLoading={state.context.isLoading}
              onLogin={handleLogin}
              onOAuthLogin={handleOAuthLogin}
            />
          }
        />
        <Route
          path="/reset-password"
          element={
            <ResetPassword
              isLoading={state.context.isLoading}
              resetForm={!!state.value.match(State.EMAIL_SENT)}
              onResetPasswordRequest={handleResetPasswordRequest}
              onResetPassword={handleResetPassword}
            />
          }
        />
        <Route
          path="/activate"
          element={
            <ActivateAccount
              isLoading={state.context.isLoading}
              customerName={customerName}
              accountExists={accountExists}
              passwordFallbackHref={
                accountExists
                  ? '/auth/login'
                  : `/auth/reset-password?token=${invitationToken}`
              }
              onOAuthLogin={handleOAuthLogin}
            />
          }
        />
      </Routes>
      {contextHolder}
    </>
  );
};

export default AuthRoutes;
